import React, { useState, useEffect } from 'react';
import { X, Package, User, MapPin, Check, Loader2, AlertTriangle } from 'lucide-react';
import { ShipmentPackageDto, OrderLineDto } from '../../../../types/packing';
import { packingService } from '../../../../services/packingService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';

interface PackageInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pkg: ShipmentPackageDto | null;
  /** Whether the package is confirmed (caller knows session state too). */
  confirmed: boolean;
}

/**
 * Kolli-info-dialog: viser nyttig info om et kolli for pakke-operatøren —
 * hvem som plukket, hvilken plukkliste/ordre kolliet er knyttet mot, pakkesone,
 * status og de koblede linjene. Kun visning; ingen mutasjon.
 *
 * Linjer hentes via det eksisterende /packing/orders/{orderNo}/lines-endepunktet.
 * Linjer som er pakket i nettopp dette kolliet (OrderLineDto.packageNo === packageId)
 * markeres.
 */
export const PackageInfoModal: React.FC<PackageInfoModalProps> = ({ isOpen, onClose, pkg, confirmed }) => {
  const { t } = useTranslation();
  const { getDisplayName } = useOperatorDisplayNames();
  const [lines, setLines] = useState<OrderLineDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !pkg) return;
    let active = true;

    // Ordre(r) kolliet er knyttet til: standalone har én orderNo; batch har flere via orders[].
    const orderNos = pkg.orderNo != null
      ? [pkg.orderNo]
      : Array.from(new Set(pkg.orders.map(o => o.orderNo)));

    if (orderNos.length === 0) {
      setLines([]);
      return;
    }

    setLoading(true);
    setError(null);
    Promise.all(orderNos.map(orderNo => packingService.getOrderLines(orderNo, pkg.pListID)))
      .then(results => {
        if (!active) return;
        setLines(results.flat());
      })
      .catch(err => {
        if (!active) return;
        console.error('Failed to load package lines:', err);
        setError(t('warehouse.modules.packing.packageInfo.loadLinesFailed', {}, 'Kunne ikke laste linjer for kolliet.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [isOpen, pkg, t]);

  if (!isOpen || !pkg) return null;

  const pickedByName = pkg.pickedByOperatorId
    ? getDisplayName(pkg.pickedByOperatorId)
    : t('warehouse.modules.packing.packageInfo.notRegistered', {}, 'Ikke registrert');

  const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>
      <span className="text-sm text-gray-500 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 break-words">{value}</span>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Package className="text-blue-600" size={22} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {t('warehouse.modules.packing.packageInfo.title', { '0': pkg.visiblePackageNo.toString() }, `Kolli ${pkg.visiblePackageNo}`)}
              </h3>
              <p className="text-xs text-gray-500">ID: {pkg.packageId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label={t('common.buttons.close', {}, 'Lukk')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-3 overflow-y-auto">
          {/* Info-felter */}
          <div className="divide-y divide-gray-100">
            <InfoRow
              icon={<User size={15} />}
              label={t('warehouse.modules.packing.packageInfo.pickedBy', {}, 'Plukket av')}
              value={pickedByName}
            />
            <InfoRow
              icon={<Package size={15} />}
              label={t('warehouse.modules.packing.packageInfo.pickList', {}, 'Plukkliste')}
              value={pkg.pListID}
            />
            {pkg.orderNo != null && (
              <InfoRow
                icon={<Package size={15} />}
                label={t('warehouse.modules.packing.packageInfo.order', {}, 'Ordre')}
                value={pkg.orders[0]?.customerName ? `${pkg.orderNo} · ${pkg.orders[0].customerName}` : pkg.orderNo}
              />
            )}
            {pkg.packZoneID && (
              <InfoRow
                icon={<MapPin size={15} />}
                label={t('warehouse.modules.packing.packageInfo.packZone', {}, 'Pakkesone')}
                value={pkg.packZoneID}
              />
            )}
            <InfoRow
              icon={confirmed ? <Check size={15} className="text-green-600" /> : <Package size={15} />}
              label={t('common.fields.status', {}, 'Status')}
              value={
                <span className={confirmed ? 'text-green-700' : 'text-orange-700'}>
                  {confirmed
                    ? t('warehouse.modules.packing.packageInfo.confirmed', {}, 'Bekreftet')
                    : t('warehouse.modules.packing.packageInfo.notConfirmed', {}, 'Ikke bekreftet')}
                </span>
              }
            />
          </div>

          {/* Batch: flere ordre */}
          {pkg.orderNo == null && pkg.orders.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.packing.packageInfo.orders', {}, 'Ordrer i kolliet')}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {pkg.orders.map(o => (
                  <span key={o.orderNo} className="text-xs bg-gray-100 text-gray-700 rounded px-2 py-0.5">
                    {o.customerName ? `${o.orderNo} · ${o.customerName}` : o.orderNo}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Koblede linjer */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Package size={15} />
              {t('warehouse.modules.packing.packageInfo.linkedLines', {}, 'Koblede linjer')}
            </h4>

            {loading && (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-3">
                <Loader2 className="animate-spin" size={16} />
                {t('warehouse.modules.packing.packageInfo.loadingLines', {}, 'Laster linjer...')}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-800">
                <AlertTriangle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {!loading && !error && lines.length === 0 && (
              <p className="text-sm text-gray-400 py-2">
                {t('warehouse.modules.packing.packageInfo.noLines', {}, 'Ingen linjer funnet.')}
              </p>
            )}

            {!loading && !error && lines.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600">
                      <th className="text-left px-2 py-1 font-medium border-b border-gray-200">{t('warehouse.modules.packing.packageInfo.colLine', {}, 'Linje')}</th>
                      <th className="text-left px-2 py-1 font-medium border-b border-gray-200">{t('warehouse.modules.packing.packageInfo.colItem', {}, 'Vare')}</th>
                      <th className="text-right px-2 py-1 font-medium border-b border-gray-200">{t('warehouse.modules.packing.packageInfo.colQty', {}, 'Antall')}</th>
                      <th className="text-right px-2 py-1 font-medium border-b border-gray-200">{t('warehouse.modules.packing.packageInfo.colPicked', {}, 'Plukket')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, idx) => {
                      const inThisPackage = line.packageNo === pkg.packageId;
                      return (
                        <tr
                          key={`${line.lineNo}-${idx}`}
                          className={`border-b border-gray-100 ${inThisPackage ? 'bg-blue-50' : ''}`}
                          title={inThisPackage ? t('warehouse.modules.packing.packageInfo.inThisPackage', {}, 'Pakket i dette kolliet') : undefined}
                        >
                          <td className="px-2 py-1 text-gray-600">#{line.lineNo}</td>
                          <td className="px-2 py-1">
                            <div className="font-medium text-gray-900">{line.itemNo}</div>
                            {line.itemDescription && <div className="text-gray-500">{line.itemDescription}</div>}
                          </td>
                          <td className="px-2 py-1 text-right text-gray-700">{line.quantity}</td>
                          <td className="px-2 py-1 text-right text-gray-700">{line.quantityPicked}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('common.buttons.close', {}, 'Lukk')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageInfoModal;
